import React, { useState, useContext, ReactNode } from 'react';
import { useConnectedUser } from '../../context/connected-user.context';
import { ConnectWalletButton } from '../connect-wallet/connect-wallet';
import { Link } from 'react-router-dom';
import logo from '../../assets/logo.png';
import { Sun, Moon, User, LogOut, Copy } from 'lucide-react';

export const ThemeContext = React.createContext({
  isDarkMode: false,
  toggleDarkMode: () => {}
});

interface ThemeProviderProps {
  children: ReactNode;
}
export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const [isDarkMode, setIsDarkMode] = useState(false);
  
  const toggleDarkMode = () => {
    setIsDarkMode(!isDarkMode);
  };
  
  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleDarkMode }}>
      <div className={isDarkMode ? 'dark' : ''}>
        {children}
      </div>
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);

const CdkBalance: React.FC<{ userBalance: number }> = ({ userBalance }) => {
   return (
    <span className="flex items-center px-4 py-1 border-2 border-red-600 bg-black text-white rounded-full text-sm font-medium">
      CDK: {userBalance}
    </span>
  );
};

const UsdcBalance: React.FC<{ userBalance: number }> = ({ userBalance }) => {
  return (
    <span className="flex items-center px-4 py-1 border-2 border-red-600 bg-black text-white rounded-full text-sm font-medium">
      USDC: {userBalance}
    </span>
  );
};

const CDKStaked: React.FC<{ countCDKStaked: number }> = ({ countCDKStaked }) => {
  return (
    <span className="flex items-center px-4 py-1 border-2 border-red-600 bg-black text-white rounded-full text-sm font-medium">
      STAKED: {countCDKStaked}
    </span>
  );
};

const WithdrawableAmount: React.FC<{ withdrawableAmount: number }> = ({ withdrawableAmount }) => {
  return (
    <span className="flex items-center px-4 py-1 border-2 border-red-600 bg-black text-white rounded-full text-sm font-medium">
      Withdrawable CDK: {withdrawableAmount}
    </span>
  );
};

const NavbarLink: React.FC<{ link: string; label: string }> = ({ link, label }) => {
  return (
    <Link
      to={link}
      className="relative px-4 py-2 text-white font-medium hover:text-blue-300 transition-colors duration-200 after:absolute after:bottom-0 after:left-0 after:h-0.5 after:w-0 after:bg-blue-400 after:transition-all hover:after:w-full"
      aria-current="page"
    >
      {label}
    </Link>
  );
};

// Nouveau composant pour afficher le compte connecté
const ConnectedAccount: React.FC<{ walletAddress: string; onDisconnect: () => void }> = ({ walletAddress, onDisconnect }) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  const shortenAddress = (address: string) => {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error('Failed to copy address:', err);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center space-x-2 px-3 py-2 bg-gray-800 border border-gray-600 rounded-lg text-white hover:bg-gray-700 transition-colors"
      >
        <User size={16} />
        <span className="text-sm font-medium">{shortenAddress(walletAddress)}</span>
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {showDropdown && (
        <div className="absolute right-0 mt-2 w-64 bg-gray-800 border border-gray-600 rounded-lg shadow-lg z-50">
          <div className="p-3 border-b border-gray-600">
            <p className="text-xs text-gray-400 mb-1">Wallet Address</p>
            <div className="flex items-center justify-between">
              <p className="text-sm text-white font-mono">{shortenAddress(walletAddress)}</p>
              <button
                onClick={copyToClipboard}
                className="p-1 hover:bg-gray-700 rounded transition-colors"
                title="Copy address"
              >
                <Copy size={14} className="text-gray-400" />
              </button>
            </div>
            {copySuccess && (
              <p className="text-xs text-green-400 mt-1">Copied!</p>
            )}
          </div>
          <div className="p-2">
            <button
              onClick={() => {
                onDisconnect();
                setShowDropdown(false);
              }}
              className="flex items-center space-x-2 w-full px-3 py-2 text-left text-red-400 hover:bg-gray-700 rounded transition-colors"
            >
              <LogOut size={16} />
              <span className="text-sm">Disconnect</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export const Navbar = () => {
  const { connectedUser, cdkBalance, usdcBalance, cdkStaked, withdrawableCDKs, disconnectUser } = useConnectedUser();
  const [isOpen, setIsOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useTheme();

  return (
    <nav className={`${isDarkMode ? 'bg-gray-900' : 'bg-gray-900'} border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-600'} shadow-lg transition-colors duration-300 fixed z-30`}>
      {/* Precise width container */}
      <div className="mx-auto px-4" style={{ width: '1480px', maxWidth: '98vw' }}>
        <div className="flex items-center justify-between py-3">
          {/* Left side - Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="h-16 w-16 rounded-full p-1 shadow-md flex items-center justify-center">
                <img src={logo} className="h-14 w-14 object-cover" alt="CodeNekt Logo" />
              </div>
              <h2 className="text-2xl font-bold bg-clip-text text-transparent" 
                  style={{ backgroundImage: 'linear-gradient(to right, #ff0000, #ff8c00)' }}>
                CDK
              </h2>
            </Link>
          </div>

          {/* Right side - Controls */}
          <div className="flex items-center space-x-3">
            {!connectedUser ? (
              <ConnectWalletButton />
            ) : (
              <>
                <div className="hidden md:flex items-center space-x-2">
                  <CdkBalance userBalance={cdkBalance} />
                  <CDKStaked countCDKStaked={cdkStaked} />
                  <WithdrawableAmount withdrawableAmount={withdrawableCDKs} />
                  <UsdcBalance userBalance={usdcBalance} />
                  {process.env.REACT_APP_ENV !== 'prod' && (
                    <NavbarLink link="/faucet" label="Get USDC" />
                  )}
                </div>
                
                {/* Theme toggle button */}
                <button
                  onClick={toggleDarkMode}
                  className="p-1.5 rounded-full text-white hover:bg-gray-700 transition-colors"
                  aria-label={isDarkMode ? "Light Mode" : "Dark Mode"}
                >
                  {isDarkMode ? <Sun size={18} /> : <Moon size={18} />}
                </button>

                {/* Connected account dropdown */}
                <div className="hidden md:block">
                  <ConnectedAccount 
                    walletAddress={connectedUser.walletAddress}
                    onDisconnect={disconnectUser}
                  />
                </div>

                {/* Mobile menu button */}
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="md:hidden p-1.5 rounded-lg text-white hover:bg-gray-700"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>

        {/* Mobile menu */}
        {connectedUser && isOpen && (
          <div className="md:hidden py-3 space-y-3 border-t border-gray-700">
            {/* Account info for mobile */}
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <User size={16} className="text-gray-400" />
                <span className="text-sm text-white font-mono">
                  {connectedUser.walletAddress.slice(0, 6)}...{connectedUser.walletAddress.slice(-4)}
                </span>
              </div>
              <button
                onClick={disconnectUser}
                className="flex items-center space-x-1 px-2 py-1 text-red-400 hover:bg-gray-700 rounded text-sm"
              >
                <LogOut size={14} />
                <span>Disconnect</span>
              </button>
            </div>
            
            {/* Balances */}
            <div className="grid grid-cols-2 gap-2">
              <CdkBalance userBalance={cdkBalance} />
              <CDKStaked countCDKStaked={cdkStaked} />
              <WithdrawableAmount withdrawableAmount={withdrawableCDKs} />
              <UsdcBalance userBalance={usdcBalance} />
            </div>
            
            {process.env.REACT_APP_ENV !== 'prod' && (
              <NavbarLink link="/faucet" label="Get USDC" />
            )}
          </div>
        )}
      </div>
    </nav>
  );
};